import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Megaphone, Users } from 'lucide-react';
import { useTenant } from '../../components/TenantContext.jsx';
import { PageHeader } from '../../components/ui/PageHeader.jsx';
import { fetchRespondents, fetchSurveyCampaigns, downloadRespondentsCsv } from '../../api/survey.js';
import { FilterBar } from '../../components/survey/FilterBar.jsx';
import { SurveyStatsCards } from '../../components/survey/SurveyStatsCards.jsx';
import { RespondentsTable } from '../../components/survey/RespondentsTable.jsx';
import { Pagination } from '../../components/survey/Pagination.jsx';
import { AnalyticsPanel } from '../../components/survey/AnalyticsPanel.jsx';
import { CampaignStatsPanel } from '../../components/survey/CampaignStatsPanel.jsx';
import { SurveySessionDetailModal } from '../../components/survey/SurveySessionDetailModal.jsx';
import { fetchAllOwnedIvrAvatars } from '../../api/ivrAgents.js';

const TABS = [
    { key: 'respondents', labelKey: 'tabs.respondents', icon: Users },
    { key: 'analytics', labelKey: 'tabs.analytics', icon: BarChart3 },
    { key: 'campaigns', labelKey: 'tabs.campaigns', icon: Megaphone },
];

export default function SurveyDataPage() {
    const { t } = useTranslation('survey');
    const { avatarId } = useParams();
    const { hostId } = useTenant();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = searchParams.get('tab') || 'respondents';

    const [avatarName, setAvatarName] = useState('');

    // 載 avatar 顯示名稱（從 tenant avatars 列表撈一筆 match）
    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        (async () => {
            try {
                const list = await fetchAllOwnedIvrAvatars({ refresh: false, hostId });
                if (cancelled) return;
                const av = (Array.isArray(list) ? list : []).find((a) => a.avatarId === avatarId);
                if (av) setAvatarName(av.name || t('untitled'));
            } catch (e) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [avatarId, hostId, t]);

    const setTab = (tabKey) => {
        searchParams.set('tab', tabKey);
        setSearchParams(searchParams, { replace: true });
    };

    return (
        <div>
            <PageHeader
                eyebrow={t('page.eyebrow')}
                title={avatarName || t('common:state.loading')}
                description={t('page.description')}
                actions={
                    <button
                        onClick={() => navigate('/survey-data')}
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-ink-700 bg-white hover:bg-cream-50 border border-cream-200 rounded-md transition"
                    >
                        <ArrowLeft size={13} />
                        {t('page.backToList')}
                    </button>
                }
            />

            {/* Tabs */}
            <div className="border-b border-cream-200 mb-5 flex items-center gap-1">
                {TABS.map((t_) => (
                    <button
                        key={t_.key}
                        onClick={() => setTab(t_.key)}
                        className={[
                            'flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition',
                            tab === t_.key
                                ? 'border-accent-500 text-accent-700'
                                : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-cream-300',
                        ].join(' ')}
                    >
                        <t_.icon size={14} />
                        {t(t_.labelKey)}
                    </button>
                ))}
            </div>

            {tab === 'respondents' && <RespondentsTabContent avatarId={avatarId} hostId={hostId} />}
            {tab === 'analytics' && <AnalyticsPanel avatarId={avatarId} />}
            {tab === 'campaigns' && <CampaignStatsPanel avatarId={avatarId} hostId={hostId} />}
        </div>
    );
}

// ───────── 名單 tab ─────────

function RespondentsTabContent({ avatarId, hostId }) {
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const pageSize = 20;

    const [respondents, setRespondents] = useState([]);
    const [pagination, setPagination] = useState({ page: 1, pageSize, total: 0, totalPages: 1 });
    const [stats, setStats] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // 詳情 Modal 開哪一筆 session（null = 關閉）
    const [openSessionId, setOpenSessionId] = useState(null);

    // 載 campaigns（給 filter dropdown 跟 row 顯示 campaign name 用）
    useEffect(() => {
        if (!hostId) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await fetchSurveyCampaigns(avatarId, hostId);
                if (!cancelled) setCampaigns(r.campaigns || []);
            } catch (e) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [avatarId, hostId]);

    // 載 respondents
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(null);
            try {
                const r = await fetchRespondents(avatarId, {
                    ...filters,
                    page,
                    pageSize,
                    includeFake: '1',  // 名單頁包含假資料（看 UI 效果）
                });
                if (cancelled) return;
                setRespondents(r.respondents || []);
                setPagination(r.pagination || { page, pageSize, total: 0, totalPages: 1 });
                setStats(r.stats || null);
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [avatarId, filters, page]);

    const handleFilterChange = (patch) => {
        setFilters((cur) => ({ ...cur, ...patch }));
        setPage(1);
    };

    const handleExport = useMemo(
        () => () => downloadRespondentsCsv(avatarId, { ...filters, includeFake: '1' }),
        [avatarId, filters],
    );

    return (
        <>
            <FilterBar
                filters={filters}
                onChange={handleFilterChange}
                campaigns={campaigns}
                onExport={handleExport}
            />

            <SurveyStatsCards stats={stats} />

            {error && (
                <div className="bg-danger-100/40 border border-danger-500/30 text-sm text-danger-500 rounded-md px-4 py-2 mb-3">
                    {error.message}
                </div>
            )}

            <RespondentsTable
                respondents={respondents}
                loading={loading}
                avatarId={avatarId}
                hostId={hostId}
                campaigns={campaigns}
                onOpenSession={setOpenSessionId}
            />

            <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                total={pagination.total}
                onChange={setPage}
            />

            <SurveySessionDetailModal
                open={!!openSessionId}
                onClose={() => setOpenSessionId(null)}
                avatarId={avatarId}
                sessionId={openSessionId}
                hostId={hostId}
            />
        </>
    );
}
